import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  Tabs,
  Tab,
  useTheme,
  InputAdornment,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useLogin, useRegister, useSendVerificationCode } from '../hooks/useAuth';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `auth-tab-${index}`,
    'aria-controls': `auth-tabpanel-${index}`,
  };
}

const SignIn: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [verificationButtonDisabled, setVerificationButtonDisabled] = useState(false);
  const [verificationCountdown, setVerificationCountdown] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Reset errors when switching tabs
    setLoginError('');
    setRegisterError('');
    setLoginSuccess(false);
    setRegisterSuccess(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Use the login mutation
  const login = useLogin();
  
  // Set success state when login is successful
  useEffect(() => {
    if (login.isSuccess) {
      setLoginSuccess(true);
      setLoginError('');
    }
  }, [login.isSuccess]);
  
  // Set error state when login fails
  useEffect(() => {
    if (login.isError) {
      setLoginError((login.error as Error).message || 'Login failed');
      setLoginSuccess(false);
    }
  }, [login.isError, login.error]);
  
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple validation
    if (!loginEmail || !loginPassword) {
      setLoginError('Please fill in all fields');
      return;
    }
    
    // Call the login mutation
    login.mutate({ email: loginEmail, password: loginPassword });
  };

  // Use the register and verification code mutations
  const register = useRegister();
  const sendVerification = useSendVerificationCode();
  
  // Set success state when registration is successful
  useEffect(() => {
    if (register.isSuccess) {
      setRegisterSuccess(true);
      setRegisterError('');
      
      // Switch to login tab after successful registration
      setTimeout(() => {
        setTabValue(0);
        setRegisterSuccess(false);
      }, 2000);
    }
  }, [register.isSuccess]);
  
  // Set error state when registration fails
  useEffect(() => {
    if (register.isError) {
      setRegisterError((register.error as Error).message || 'Registration failed');
      setRegisterSuccess(false);
    }
  }, [register.isError, register.error]);
  
  // Handle verification code sending
  useEffect(() => {
    if (sendVerification.isSuccess) {
      setVerificationSuccess(true);
      setVerificationError('');
      
      // Disable the button for 1 hour (3600 seconds)
      setVerificationButtonDisabled(true);
      setVerificationCountdown(3600);
      
      // Start countdown
      const countdownInterval = setInterval(() => {
        setVerificationCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setVerificationButtonDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Clean up interval on component unmount
      return () => clearInterval(countdownInterval);
    }
  }, [sendVerification.isSuccess]);
  
  // Set error state when verification code sending fails
  useEffect(() => {
    if (sendVerification.isError) {
      setVerificationError((sendVerification.error as Error).message || 'Failed to send verification code');
      setVerificationSuccess(false);
    }
  }, [sendVerification.isError, sendVerification.error]);
  
  // Format countdown time as MM:SS
  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  const handleSendVerificationCode = () => {
    // Simple validation
    if (!registerEmail) {
      setVerificationError('Please enter your email address');
      return;
    }
    
    // Reset states
    setVerificationError('');
    setVerificationSuccess(false);
    
    // Call the send verification code mutation
    sendVerification.mutate(registerEmail);
  };

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple validation
    if (!registerName || !registerEmail || !registerPassword || !registerConfirmPassword || !verificationCode) {
      setRegisterError('Please fill in all fields');
      return;
    }
    
    if (registerPassword !== registerConfirmPassword) {
      setRegisterError('Passwords do not match');
      return;
    }
    
    // Call the register mutation
    register.mutate({
      fullname: registerName,
      email: registerEmail,
      password: registerPassword,
      verificationCode: verificationCode
    });
  };

  return (
    <Box
      sx={{
        py: 8,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(45deg, #303030 30%, #212121 90%)'
          : 'linear-gradient(45deg, #f5f5f5 30%, #e0e0e0 90%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            background: theme.palette.background.paper,
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Login" {...a11yProps(0)} />
              <Tab label="Register" {...a11yProps(1)} />
            </Tabs>
          </Box>

          {/* Login Panel */}
          <TabPanel value={tabValue} index={0}>
            <Box component="form" onSubmit={handleLogin} noValidate>
              <Typography variant="h4" component="h1" align="center" gutterBottom>
                Welcome Back
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 3 }}>
                Sign in to continue to Web Starter
              </Typography>

              {loginError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {loginError}
                </Alert>
              )}

              {loginSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Login successful! Redirecting...
                </Alert>
              )}
              
              {login.isPending && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Logging in...
                </Alert>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
                <Link href="#" variant="body2">
                  Forgot password?
                </Link>
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                sx={{ py: 1.5 }}
                disabled={login.isPending}
              >
                {login.isPending ? 'Signing In...' : 'Sign In'}
              </Button>

              <Box sx={{ mt: 3, mb: 2 }}>
                <Divider>
                  <Typography variant="body2" color="textSecondary">
                    OR
                  </Typography>
                </Divider>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box>
                  <Typography variant="body2">
                    Don't have an account?{' '}
                    <Link href="#" onClick={(e) => { e.preventDefault(); setTabValue(1); }}>
                      Sign Up
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TabPanel>

          {/* Register Panel */}
          <TabPanel value={tabValue} index={1}>
            <Box component="form" onSubmit={handleRegister} noValidate>
              <Typography variant="h4" component="h1" align="center" gutterBottom>
                Create Account
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 3 }}>
                Sign up to get started with Web Starter
              </Typography>

              {registerError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {registerError}
                </Alert>
              )}

              {registerSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Registration successful! Redirecting to login...
                </Alert>
              )}
              
              {register.isPending && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Registering...
                </Alert>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Full Name"
                name="name"
                autoComplete="name"
                autoFocus
                value={registerName}
                onChange={(e) => setRegisterName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="register-email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={registerEmail}
                onChange={(e) => setRegisterEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                <TextField
                  required
                  sx={{ flex: 2 }}
                  id="verification-code"
                  label="Verification Code"
                  name="verification-code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ flex: 1, height: 56 }}
                  onClick={handleSendVerificationCode}
                  disabled={verificationButtonDisabled || sendVerification.isPending}
                >
                  {sendVerification.isPending ? 'Sending...' : 
                   verificationButtonDisabled ? `Resend in ${formatCountdown(verificationCountdown)}` : 'Get Code'}
                </Button>
              </Box>
              
              {verificationError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {verificationError}
                </Alert>
              )}
              
              {verificationSuccess && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Verification code sent to your email!
                </Alert>
              )}
              <TextField
                margin="normal"
                required
                fullWidth
                name="register-password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="register-password"
                value={registerPassword}
                onChange={(e) => setRegisterPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirm-password"
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                id="confirm-password"
                value={registerConfirmPassword}
                onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={register.isPending}
              >
                {register.isPending ? 'Signing Up...' : 'Sign Up'}
              </Button>

              <Box sx={{ mt: 3, mb: 2 }}>
                <Divider>
                  <Typography variant="body2" color="textSecondary">
                    OR
                  </Typography>
                </Divider>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box>
                  <Typography variant="body2">
                    Already have an account?{' '}
                    <Link href="#" onClick={(e) => { e.preventDefault(); setTabValue(0); }}>
                      Sign In
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TabPanel>
        </Paper>
      </Container>
    </Box>
  );
};

export default SignIn;
